import {
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@dls/react-core';
import { em } from '@dls/react-theme';
import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { getUsers, User } from '../../../api/studykit';
import useRequest from '../../../api/useRequest';
import { StudyPageContext } from '../../../App';
import {
  EnhancedTableHead,
  HeadCell,
  Order,
  sortedTableStyles,
} from '../../../components/sorted-table/sorted-table-head';
import { stableSort, getComparator } from '../../../components/sorted-table/sorting-utils';
import { StyledButtonContainerRight } from '../../../components/styled/styles';
import { usePermission } from '../../../context/authorization';
import { ColorDLS } from '../../../styling/color';
import { Icon } from '@dls/react-icon';
import { EditStudyUserDialog } from '../../../components/dialogs/edit-user-roles-dialog';
import { RemoveUserDialog } from '../../../components/dialogs/remove-user-dialog';
import { AddUserDialog } from '../../../components/dialogs/add-user-dialog';

const StyledTableRow = styled(TableRow)`
  :hover {
    cursor: pointer;
    background: ${ColorDLS.Gray10};
  }
`;

const StyledToolbar = styled(Toolbar)`
  display: 'flex';
  justify-content: space-between;
  padding-top: 16px;
`;

const headCells: HeadCell<User>[] = [
  { label: 'Email', type: 'sortable', numeric: false, disablePadding: true, id: 'email' },
  { label: 'Roles', type: 'sortable', numeric: false, disablePadding: true, id: 'groups' },
  { label: '', type: 'other', disablePadding: true, id: 'actions', width: 175 },
];

export const Users = () => {
  const tableClasses = sortedTableStyles();
  const { study } = useContext(StudyPageContext);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof User>('email');
  const { data: users, error, mutate } = useRequest(getUsers(study.id));
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState<User>();
  const [userToRemove, setUserToRemove] = useState<User>();

  const canUpdateUsers = usePermission('Users.Update');
  const canDeleteUsers = usePermission('Users.Delete');
  const canCreateusers = usePermission('Users.Create');

  const handleRequestSort = useCallback(
    (event: React.MouseEvent<unknown>, property: keyof User) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy],
  );

  return (
    <Paper className={'dlsTable'}>
      <StyledToolbar className={'dlsTableHeader'}>
        <Typography
          weight="bold"
          style={{
            fontSize: em(18),
            // marginBottom: 'none',
          }}
        >
          Users
        </Typography>
        {canCreateusers && (
          <StyledButtonContainerRight>
            <Button
              dls_variant="primary"
              startIcon={<Icon name={'Plus'} size={'m'} />}
              onClick={() => setAddUserDialogOpen(true)}
            >
              Add User
            </Button>
          </StyledButtonContainerRight>
        )}
      </StyledToolbar>
      {users === undefined ? (
        <LinearProgress variant={'indeterminate'} />
      ) : (
        <TableContainer style={{ width: `unset`, padding: 24 }}>
          {canUpdateUsers && (
            <EditStudyUserDialog
              open={!!userToUpdate}
              user={userToUpdate}
              studyId={study.id}
              onClose={() => setUserToUpdate(undefined)}
              onRolesUpdated={() => mutate()}
            />
          )}
          {canDeleteUsers && (
            <RemoveUserDialog
              open={!!userToRemove}
              user={userToRemove}
              studyId={study.id}
              onRemoved={(deletedUser) =>
                mutate(users.filter((u) => u.email !== deletedUser.email))
              }
              onClose={() => setUserToRemove(undefined)}
            />
          )}
          {canCreateusers && (
            <AddUserDialog
              open={addUserDialogOpen}
              studyId={study.id}
              onClose={() => setAddUserDialogOpen(false)}
              onUserCreated={async (newUser) => {
                mutate([...users, newUser]);
              }}
            />
          )}
          <Table>
            <EnhancedTableHead
              onRequestSort={handleRequestSort}
              classes={tableClasses}
              order={order}
              orderBy={orderBy}
              headCells={headCells}
            />
            <TableBody>
              {!users?.length || error ? (
                <TableRow data-testid={'row-no-data'}>
                  <TableCell
                    scope="row"
                    data-testid="cell-no-data"
                    colSpan={headCells.length}
                    style={{ textAlign: `center` }}
                  >
                    {error
                      ? 'Something went wrong while fetching users of this study'
                      : 'There are no users assigned to this study yet'}
                  </TableCell>
                </TableRow>
              ) : (
                stableSort(users, getComparator(order, orderBy)).map((row) => {
                  return (
                    <UserTableRow
                      user={row}
                      key={row.email}
                      enableUpdate={canUpdateUsers}
                      enableDelete={canDeleteUsers}
                      onUpdateAction={setUserToUpdate}
                      onDeleteAction={setUserToRemove}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

const UserTableRow: React.FC<{
  user: User;
  enableUpdate: boolean;
  enableDelete: boolean;
  onUpdateAction: (u: User) => void;
  onDeleteAction: (u: User) => void;
}> = ({ user: row, enableUpdate, enableDelete, onUpdateAction, onDeleteAction }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <StyledTableRow
      data-testid={`row-${row.email}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell scope="row">{row.email ?? '-'}</TableCell>
      <TableCell scope="row">
        {row.groups.map((g) => (
          <Chip key={`${row.email}-${g}`} label={g} />
        ))}
      </TableCell>
      <TableCell align="right">
        {isHovered && (
          <>
            {enableUpdate && (
              <Button
                onClick={() => {
                  onUpdateAction(row);
                }}
              >
                <Icon name="Edit" color={ColorDLS.Gray45} />
              </Button>
            )}
            {enableDelete && (
              <Button
                onClick={() => {
                  onDeleteAction(row);
                }}
              >
                <Icon name="DeleteTrash" color={ColorDLS.Gray45} />
              </Button>
            )}
          </>
        )}
      </TableCell>
    </StyledTableRow>
  );
};
